/**
 * RolePermissions Component
 * Main component for role and permission management
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllRoles, updateRole, createRole, reorderRoles } from '../../services/roleService';
import { useMessageApi } from '../../shared/providers/MessageProvider';
import { PERMISSION_ACTIONS, PERMISSION_LABELS } from './utils/permissionHelpers';
import { usePermissions } from './hooks/usePermissions';
import { useNavigationBlocker } from './hooks/useNavigationBlocker';
import { SUPER_ADMIN_ROLE_ID, hasPermission as hasPermissionHelper } from '../../utils/roleHelpers';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';

// Components
import SearchBar from './components/SearchBar/SearchBar';
import RoleTableHeader from './components/RoleTableHeader/RoleTableHeader';
import ModuleRow from './components/ModuleRow/ModuleRow';
import PermissionRow from './components/PermissionRow/PermissionRow';
import ActionBar from './components/ActionBar/ActionBar';
import EmptyState from './components/EmptyState/EmptyState';
import RoleModal from './components/Modals/RoleModal';
import SkeletonLoader from './components/SkeletonLoader/SkeletonLoader';
import UnsavedChangesModal from './components/Modals/UnsavedChangesModal';

// Styles
import './RolePermissions.scss';

const RolePermissions = ({ userInfo = null }) => {
  const message = useMessageApi();
  // State
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [hoveredRole, setHoveredRole] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigationCallback, setPendingNavigationCallback] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  // DnD sensors for drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  );

  // Custom hook for permission logic
  const {
    permissionChanges,
    hasPermission,
    togglePermission,
    toggleModuleForRole,
    discardChanges,
    unsavedChangesCount,
    getUpdatePayload
  } = usePermissions(roles);

  /**
   * Block React Router navigation when there are unsaved changes
   * Intercepts programmatic navigation (Link clicks, navigate() calls)
   */
  useNavigationBlocker(
    unsavedChangesCount > 0,
    (proceedCallback) => {
      setShowUnsavedChangesModal(true);
      setPendingNavigationCallback(() => proceedCallback);
    }
  );

  /**
   * Handle browser-level navigation (tab close, refresh, back/forward buttons)
   * Shows browser's native confirmation dialog
   */
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (unsavedChangesCount > 0) {
        event.preventDefault();
        // Most modern browsers ignore custom messages and show their own
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [unsavedChangesCount]);

  /**
   * Block browser back/forward button navigation
   */
  useEffect(() => {
    if (unsavedChangesCount === 0) return;

    const handlePopState = () => {
      // Show modal instead of window.confirm
      setShowUnsavedChangesModal(true);
      setPendingNavigationCallback(null); // null means just stay on page
      // Push state back to stay on current page (will navigate if user confirms)
      window.history.pushState(null, '', window.location.pathname);
    };

    // Push an extra state to detect back button
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [unsavedChangesCount]);

  /**
   * Handle unsaved changes modal confirmation
   */
  const handleConfirmLeave = useCallback(() => {
    setShowUnsavedChangesModal(false);
    if (pendingNavigationCallback) {
      pendingNavigationCallback();
    } else {
      // If no callback, user pressed back button, so go back
      window.history.back();
    }
    setPendingNavigationCallback(null);
  }, [pendingNavigationCallback]);

  /**
   * Handle unsaved changes modal cancellation
   */
  const handleCancelLeave = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setPendingNavigationCallback(null);
  }, []);

  /**
   * Load roles and permissions from API
   * @param {boolean} isRefresh - If true, shows table skeleton instead of full page loader
   */
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Start timer for minimum skeleton display time
      const startTime = Date.now();
      const minDisplayTime = 1000;
      
      const rolesData = await getAllRoles();
      if (rolesData?.roles) {
        setRoles(rolesData.roles);
      }

      // Build permission structure
      const allPermissions = [];
      const grouped = {};
      
      Object.entries(PERMISSION_ACTIONS).forEach(([module, actions]) => {
        grouped[module] = [];
        
        actions.forEach(action => {
          const permission = {
            id: `${module}_${action}`,
            module,
            action,
            permission_key: `${module}:${action}`,
            label: PERMISSION_LABELS[module]?.[action] || action
          };
          allPermissions.push(permission);
          grouped[module].push(permission);
        });
      });
      
      setPermissions(allPermissions);
      setGroupedPermissions(grouped);

      // Ensure skeleton shows for at least minDisplayTime
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } catch (error) {
      console.error('Error loading roles and permissions:', error);
      message.error('Failed to load roles and permissions', 2.5);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Filter permissions based on search
   */
  const filteredGroupedPermissions = useMemo(() => {
    if (!searchTerm.trim()) return groupedPermissions;

    const search = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(groupedPermissions).forEach(([module, perms]) => {
      const matchingPerms = perms.filter(perm => 
        perm.action.toLowerCase().includes(search) ||
        perm.permission_key.toLowerCase().includes(search) ||
        module.toLowerCase().includes(search)
      );

      if (matchingPerms.length > 0) {
        filtered[module] = matchingPerms;
      }
    });

    return filtered;
  }, [groupedPermissions, searchTerm]);

  /**
   * Check if role is locked based on update permission
   * Super Admin (1) is always locked for everyone
   * Other roles are locked only if user doesn't have update permission
   */
  const isRoleLocked = useCallback((role) => {
    if (!role) return true;

    const targetRoleId = Number(role.id);

    if (!Number.isFinite(targetRoleId)) {
      return true;
    }

    // Super Admin (1) is always locked - cannot be edited by anyone
    if (targetRoleId === SUPER_ADMIN_ROLE_ID) {
      return true;
    }

    // If no userInfo, lock all roles for safety
    if (!userInfo) {
      return true;
    }

    // Check if user has update permission
    // If user has update permission, role is not locked (except Super Admin)
    // If user doesn't have update permission, role is locked
    const hasUpdatePermission = hasPermissionHelper(userInfo, 'roles_and_permissions', 'update');
    return !hasUpdatePermission;
  }, [userInfo]);

  /**
   * Format role name
   */
  const formatRoleName = useCallback((name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  /**
   * Format action name
   */
  const formatAction = useCallback((action) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  /**
   * Handle save changes
   */
  const handleSaveChanges = useCallback(async () => {
    try {
      setSaving(true);
      
      // Check if user has update permission
      if (!userInfo || !hasPermissionHelper(userInfo, 'roles_and_permissions', 'update')) {
        message.warning('You do not have permission to update role permissions', 2.5);
        setSaving(false);
        return;
      }
      
      const changesByRole = getUpdatePayload();
      const roleCount = Object.keys(changesByRole).length;

      // Prevent saving updates for Super Admin role only
      // Update permission is sufficient - no hierarchy check needed
      const unauthorizedRoleIds = Object.keys(changesByRole).filter((roleId) => {
        const role = roles.find((r) => Number(r.id) === Number(roleId));
        if (!role) return true;
        
        const targetRoleId = Number(role.id);
        if (!Number.isFinite(targetRoleId)) return true;
        
        // Super Admin is always locked - cannot be updated
        if (targetRoleId === SUPER_ADMIN_ROLE_ID) return true;
        
        // All other roles can be updated if user has update permission
        return false;
      });

      if (unauthorizedRoleIds.length > 0) {
        const unauthorizedRoles = unauthorizedRoleIds
          .map(roleId => {
            const role = roles.find(r => Number(r.id) === Number(roleId));
            return role ? formatRoleName(role.name) : 'Unknown';
          })
          .join(', ');
        message.error(`You don't have permission to modify permissions for: ${unauthorizedRoles}`, 2.5);
        discardChanges();
        setSaving(false);
        return;
      }

      // Track updated role names for meaningful success message
      const updatedRoleNames = [];

      const updates = Object.entries(changesByRole).map(async ([roleId, permissionUpdates]) => {
        const role = roles.find(r => r.id === Number(roleId));
        if (!role) return null;

        updatedRoleNames.push(formatRoleName(role.name));

        const updatedPermissions = JSON.parse(JSON.stringify(role.permissions));
        
        Object.entries(permissionUpdates).forEach(([module, actions]) => {
          if (!updatedPermissions[module]) {
            updatedPermissions[module] = {};
          }
          Object.entries(actions).forEach(([action, value]) => {
            updatedPermissions[module][action] = value;
          });
        });

        // Batch updates - messages will be shown after all updates complete
        const currentUserId = userInfo?.id || null;
        return updateRole(Number(roleId), {
          permissions: updatedPermissions,
          name: role.name,
          user_id: currentUserId
        });
      });

      const results = await Promise.all(updates.filter(Boolean));
      
      // Check if all updates were successful
      const allSuccessful = results.every(result => result && result.success);
      const failedCount = results.filter(result => !result || !result.success).length;
      
      if (allSuccessful) {
        // Show meaningful success message with role names
        let successMessage;
        if (roleCount === 1) {
          successMessage = `Permissions updated successfully for ${updatedRoleNames[0]}`;
        } else if (roleCount === 2) {
          successMessage = `Permissions updated successfully for ${updatedRoleNames.join(' and ')}`;
        } else {
          successMessage = `Permissions updated successfully for ${updatedRoleNames.slice(0, -1).join(', ')}, and ${updatedRoleNames[updatedRoleNames.length - 1]}`;
        }
        message.success(successMessage, 2.5);
      } else if (failedCount === roleCount) {
        message.error('Failed to update permissions. Please try again.', 2.5);
      } else {
        message.warning(`${roleCount - failedCount} of ${roleCount} role permissions updated. Some updates failed.`, 2.5);
      }
      
      discardChanges();
      await loadData(true); // Refresh with table skeleton only
    } catch (error) {
      console.error('Error saving changes:', error);
      message.error('Failed to save changes. Please check your connection and try again.', 2.5);
    } finally {
      setSaving(false);
    }
  }, [roles, getUpdatePayload, discardChanges, loadData, formatRoleName, userInfo, message]);

  /**
   * Handle discard changes
   */
  const handleDiscard = useCallback(() => {
    discardChanges();
    message.info('Changes discarded', 2.5);
  }, [discardChanges, message]);

  /**
   * Handle edit role
   */
  const handleEditRole = useCallback((role) => {
    if (isRoleLocked(role)) {
      message.warning(`${formatRoleName(role.name)} role cannot be edited`, 2.5);
      return;
    }
    setEditingRole(role);
    setShowModal(true);
  }, [isRoleLocked, formatRoleName, message]);

  /**
   * Handle add role
   */
  const handleAddRole = useCallback(() => {
    // Check if user has create permission
    if (!userInfo || !hasPermissionHelper(userInfo, 'roles_and_permissions', 'create')) {
      message.warning('You do not have permission to create roles', 2.5);
      return;
    }
    setEditingRole(null);
    setShowModal(true);
  }, [userInfo, message]);

  /**
   * Handle role save
   */
  const handleRoleSave = useCallback(async (roleData) => {
    try {
      const currentUserId = userInfo?.id || null;
      
      let result;
      if (editingRole) {
        // Check if user has update permission for editing
        if (!userInfo || !hasPermissionHelper(userInfo, 'roles_and_permissions', 'update')) {
          message.warning('You do not have permission to update roles', 2.5);
          return;
        }
        
        result = await updateRole(editingRole.id, {
          ...roleData,
          user_id: currentUserId
        });
        
        if (result?.success) {
          message.success(result.message || 'Role updated successfully', 2.5);
          setShowModal(false);
          setEditingRole(null);
          await loadData(true); // Refresh with table skeleton only
        } else {
          message.error(result?.message || 'Failed to update role', 2.5);
        }
      } else {
        // Check if user has create permission for new role
        if (!userInfo || !hasPermissionHelper(userInfo, 'roles_and_permissions', 'create')) {
          message.warning('You do not have permission to create roles', 2.5);
          return;
        }
        
        result = await createRole({
          ...roleData,
          user_id: currentUserId
        });
        
        if (result?.success) {
          message.success(result.message || 'Role created successfully', 2.5);
          setShowModal(false);
          setEditingRole(null);
          await loadData(true); // Refresh with table skeleton only
        } else {
          message.error(result?.message || 'Failed to create role', 2.5);
        }
      }
    } catch (error) {
      console.error('Error saving role:', error);
      message.error('An error occurred while saving role. Please try again.', 2.5);
    }
  }, [editingRole, loadData, userInfo, message]);

  /**
   * Handle modal close
   */
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingRole(null);
  }, []);

  /**
   * Handle drag end for role reordering
   */
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return; // No change in position
    }

    // Check if user has reorder permission
    if (!userInfo || !hasPermissionHelper(userInfo, 'roles_and_permissions', 'reorder')) {
      message.warning('You do not have permission to reorder roles', 2.5);
      return;
    }

    // Extract role IDs from drag IDs (format: "role-{id}")
    const activeId = String(active.id).replace('role-', '');
    const overId = String(over.id).replace('role-', '');

    const activeIndex = roles.findIndex(r => String(r.id) === activeId);
    const overIndex = roles.findIndex(r => String(r.id) === overId);

    if (activeIndex === -1 || overIndex === -1) {
      return; // Invalid indices
    }

    // Check if the dragged role is Super Admin (Super Admin cannot be moved)
    const draggedRole = roles[activeIndex];
    const isSuperAdmin = Number(draggedRole.id) === SUPER_ADMIN_ROLE_ID;
    if (isSuperAdmin) {
      message.warning(`${formatRoleName(draggedRole.name)} role cannot be reordered`, 2.5);
      return;
    }

    // Reorder roles array
    const newRoles = arrayMove(roles, activeIndex, overIndex);

    // Calculate new order values for all roles based on their positions in the new array
    // Important: Update ALL roles' orders (except Super Admin) based on their new positions
    // This ensures database consistency - all roles get updated to reflect the new order
    // 
    // Note: Orders are based on array position (0-based index + 1 = 1-based order)
    // Super Admin is excluded from updates but its order (1) remains fixed
    // Other roles get orders 2, 3, 4, etc. based on their positions after Super Admin
    const roleOrders = newRoles
      .filter(role => Number(role.id) !== SUPER_ADMIN_ROLE_ID) // Exclude Super Admin
      .map((role) => {
        // Find the position of this role in the reordered array (0-based index)
        const newPosition = newRoles.findIndex(r => Number(r.id) === Number(role.id));
        
        if (newPosition === -1) {
          console.error(`Role ${role.id} not found in reordered array`);
          return null;
        }
        
        // Calculate order: array position + 1 (position 0 = order 1, position 1 = order 2, etc.)
        // This ensures sequential ordering based on visual position
        const newOrder = newPosition + 1;
        
        return {
          id: Number(role.id),
          order: newOrder
        };
      })
      .filter(Boolean); // Remove any null entries

    // If no roles to update (only Super Admin in array), return early
    if (roleOrders.length === 0) {
      message.warning('No roles to reorder', 2.5);
      return;
    }

    // Log the order updates for debugging
    console.log('Updating role orders in database:', {
      before: roles.map(r => ({ id: r.id, name: r.name, order: r.order })),
      after: newRoles.map(r => ({ id: r.id, name: r.name, position: newRoles.findIndex(nr => nr.id === r.id) })),
      updates: roleOrders
    });

    try {
      setIsReordering(true);
      const currentUserId = userInfo?.id || null;
      
      const result = await reorderRoles(roleOrders, currentUserId);

      if (result?.success) {
        // Update local state immediately with reordered roles (no skeleton)
        setRoles(newRoles);
        
        // Reload data from database silently (without showing skeleton)
        // This ensures we have the correct updated order values from DB
        try {
          const rolesData = await getAllRoles();
          if (rolesData?.roles) {
            setRoles(rolesData.roles);
          }
        } catch (reloadError) {
          console.error('Error reloading roles after reorder:', reloadError);
          // Keep the local state update even if reload fails
        }
        
        message.success('Roles reordered and saved successfully', 2.5);
      } else {
        message.error(result?.message || 'Failed to reorder roles', 2.5);
        // Reload data to get original order (with skeleton since it's an error case)
        await loadData(true);
      }
    } catch (error) {
      console.error('Error reordering roles:', error);
      message.error('Failed to reorder roles. Please try again.', 2.5);
      // Reload data to get original order (with skeleton since it's an error case)
      await loadData(true);
    } finally {
      setIsReordering(false);
    }
  }, [roles, isRoleLocked, formatRoleName, userInfo, message, loadData]);

  // Loading state
  if (loading) {
    return <SkeletonLoader />;
  }

  // No roles state
  if (roles.length === 0) {
    return (
      <div className="role-permissions-table">
        <div className="no-results-content">
          <p>No roles found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-permissions-table">
      {/* Search Bar */}
      <SearchBar
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
        onAddRole={handleAddRole}
        canCreate={userInfo && hasPermissionHelper(userInfo, 'roles_and_permissions', 'create')}
      />

      {/* Permissions Table */}
      <div className={`rp-table-container ${unsavedChangesCount > 0 ? 'has-action-bar' : ''}`}>
        {refreshing && !isReordering ? (
          // Show table skeleton when refreshing (not full page reload)
          <div className="rp-table-refresh-skeleton">
            <table className="skeleton-table">
              {/* Table Header Skeleton */}
              <thead>
                <tr>
                  <th className="skeleton-th-first">
                    <div className="skeleton-box skeleton-header-text"></div>
                  </th>
                  {[1, 2, 3, 4, 5].map((role) => (
                    <th key={role} className="skeleton-th">
                      <div className="skeleton-box skeleton-role-header"></div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body Skeleton */}
              <tbody>
                {[1, 2, 3, 4].map((module, moduleIdx) => {
                  const permissionsPerModule = [3, 4, 3, 4, 5];
                  return (
                    <React.Fragment key={module}>
                      {/* Module Header Row */}
                      <tr className="skeleton-module-row">
                        <td className="skeleton-module-name">
                          <div className="skeleton-box skeleton-module-text"></div>
                        </td>
                        {[1, 2, 3, 4, 5].map((role) => (
                          <td key={role} className="skeleton-module-cell">
                            <div className="skeleton-checkbox-module"></div>
                          </td>
                        ))}
                      </tr>

                      {/* Permission Rows */}
                      {Array.from({ length: permissionsPerModule[moduleIdx] }).map((_, permIdx) => (
                        <tr key={`${module}-${permIdx}`} className="skeleton-permission-row">
                          <td className="skeleton-permission-name">
                            <div className="skeleton-dot"></div>
                            <div className="skeleton-box skeleton-permission-text"></div>
                          </td>
                          {[1, 2, 3, 4, 5].map((role) => (
                            <td key={role} className="skeleton-permission-cell">
                              <div className="skeleton-checkbox"></div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="rp-table">
              {/* Table Header */}
              <SortableContext
                items={roles.map(r => `role-${r.id}`)}
                strategy={horizontalListSortingStrategy}
              >
                <RoleTableHeader
                  roles={roles}
                  isRoleLocked={isRoleLocked}
                  formatRoleName={formatRoleName}
                  onEditRole={handleEditRole}
                  hoveredRole={hoveredRole}
                  setHoveredRole={setHoveredRole}
                  canReorder={userInfo && hasPermissionHelper(userInfo, 'roles_and_permissions', 'reorder')}
                />
              </SortableContext>
              
              {/* Table Body */}
              <tbody>
              {Object.entries(filteredGroupedPermissions).map(([module, perms]) => (
                <React.Fragment key={module}>
                  {/* Module Header */}
                  <ModuleRow
                    module={module}
                    permissions={perms}
                    roles={roles}
                    isRoleLocked={isRoleLocked}
                    hasPermission={hasPermission}
                    toggleModuleForRole={toggleModuleForRole}
                  />
                  
                  {/* Permission Rows */}
                  {perms.map(permission => (
                    <PermissionRow
                      key={permission.id}
                      permission={permission}
                      roles={roles}
                      isRoleLocked={isRoleLocked}
                      hasPermission={hasPermission}
                      permissionChanges={permissionChanges}
                      formatAction={formatAction}
                      formatRoleName={formatRoleName}
                      togglePermission={togglePermission}
                      hoveredCell={hoveredCell}
                      setHoveredCell={setHoveredCell}
                    />
                  ))}
                </React.Fragment>
              ))}

              {/* Empty State */}
              {Object.keys(filteredGroupedPermissions).length === 0 && (
                <EmptyState
                  searchTerm={searchTerm}
                  onClearSearch={() => setSearchTerm('')}
                  rolesCount={roles.length}
                />
              )}
            </tbody>
          </table>
          </DndContext>
        )}
      </div>

      {/* Action Bar */}
      <ActionBar
        unsavedChangesCount={unsavedChangesCount}
        onDiscard={handleDiscard}
        onSave={handleSaveChanges}
        saving={saving}
      />


      {/* Role Modal */}
      {showModal && (
        <RoleModal
          visible={showModal}
          role={editingRole}
          onClose={handleModalClose}
          onSave={handleRoleSave}
        />
      )}

      {/* Unsaved Changes Confirmation Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onCancel={handleCancelLeave}
        onConfirm={handleConfirmLeave}
        changesCount={unsavedChangesCount}
      />
    </div>
  );
};

export default RolePermissions;
